import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Input, Message, Radio, Typography } from "@arco-design/web-react";
import { OverwriteApi } from "../../Api/AuthenticatedApi";
import MailVariantsEditor from "./MailVariants";
import { FollowupEmailEditor } from "./IncentiveEditor";
import { builtinSignupDefaults, designsFor } from "../builtinEmail";

const { Text } = Typography;

// The campaign's emails, one slot for each email its flow sends: the signup
// email (when the form is submitted) and, when the reward goes by email after
// they confirm, the follow-up email. Each slot uses the built-in email (the
// wording here, with the right buttons added when it's sent) or the
// campaign's own designs (with several, an A/B test). The follow-up slot
// shows only when the flow sends one.
//
//   record     the saved campaign (its emails, and which each slot uses)
//   flow       the settings as they stand: { doubleOptIn, includeIncentive, incentiveDelivery }
//   incentive  the reward as saved (its follow-up wording), onIncentive(saved)
//   onChanged  the campaign changed: reload it
export default function CampaignEmails({ campaignId, record, flow, incentive, onIncentive, onChanged }) {
    const { getAccessTokenSilently } = useAuth0();
    const f = flow || {};
    const followupSlot = !!f.doubleOptIn && !!f.includeIncentive && f.incentiveDelivery === "email";

    const setSource = (slot, value) => {
        OverwriteApi(getAccessTokenSilently, `/api/campaigns/${campaignId}/email-sources`,
            () => { Message.success(value === "builtin" ? "Using the built-in email" : "Using your own design"); onChanged(); },
            () => {},
            (err) => Message.error((err && err.message) || "Couldn't change it"),
            { [`${slot}_email_source`]: value });
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Slot
                title="Signup email"
                when={f.doubleOptIn ? "Sent when they submit the form, asking them to confirm." : "Sent when they submit the form, welcoming them."}
                source={record.signup_email_source === "custom" ? "custom" : "builtin"}
                onSource={(v) => setSource("signup", v)}
                fixField="signup_emails"
                builtin={<BuiltinSignupEmail campaignId={campaignId} record={record} doubleOptIn={!!f.doubleOptIn} reward={!!f.includeIncentive && !!incentive} onChanged={onChanged} />}
                builtinSummary={f.doubleOptIn ? "Your wording, with a Confirm button added." : "Your wording" + (f.includeIncentive ? ", with the reward's button added." : ".")}
                designs={<MailVariantsEditor campaignId={campaignId} campaign={record} mailVariants={designsFor(record, "signup")} onChanged={onChanged} showFlow={false} />}
                noDesigns={!designsFor(record, "signup").length}
            />
            {followupSlot && (
                <Slot
                    title="Follow-up email"
                    when="Sent once they confirm, bringing their reward."
                    source={record.followup_email_source === "custom" ? "custom" : "builtin"}
                    onSource={(v) => setSource("followup", v)}
                    fixField="followup"
                    builtin={incentive ? (
                        <FollowupEmailEditor key={incentive.id} campaignId={campaignId} incentive={incentive} onSaved={onIncentive} />
                    ) : (
                        <Text type="secondary">Add the reward's file or link first (Signup Settings), then set up the email that brings it.</Text>
                    )}
                    builtinSummary="Your wording, with the reward's button added."
                    designs={<MailVariantsEditor campaignId={campaignId} campaign={record} mailVariants={designsFor(record, "followup")} onChanged={onChanged} role="followup" showFlow={false} />}
                    noDesigns={!designsFor(record, "followup").length}
                />
            )}
            {!followupSlot && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    A follow-up email slot appears here when the reward goes by email after they confirm (“After they click Confirm” in Signup Settings).
                </Text>
            )}
        </div>
    );
}

// Slot is one email the flow sends: the built-in email or your own designs.
function Slot({ title, when, source, onSource, fixField, builtin, builtinSummary, designs, noDesigns }) {
    return (
        <div data-fix-field={fixField} style={{ border: "1px solid var(--color-border-2)", borderRadius: 8, padding: "14px 16px", background: "var(--color-bg-2)" }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{title}</div>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 10 }}>{when}</Text>
            <Radio.Group value={source} onChange={onSource} type="button" style={{ marginBottom: 12 }}>
                <Radio value="builtin">Built-in email</Radio>
                <Radio value="custom">Your own design</Radio>
            </Radio.Group>
            {source === "builtin" ? (
                <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>{builtinSummary}</Text>
                    {builtin}
                </div>
            ) : (
                <div>
                    {noDesigns && (
                        <Alert type="warning" style={{ marginBottom: 12 }}
                            content="There's no design yet, so nothing would be sent: make one, or switch back to the built-in email." />
                    )}
                    {designs}
                </div>
            )}
        </div>
    );
}

// BuiltinSignupEmail edits the built-in signup email's wording (blank = the
// default); the buttons are added when it's sent.
function BuiltinSignupEmail({ campaignId, record, doubleOptIn, reward, onChanged }) {
    const { getAccessTokenSilently } = useAuth0();
    const row = ((record && record.mail_variants) || []).find((v) => v && v.builtin && (v.role || "signup") === "signup");
    const [subject, setSubject] = React.useState((row && row.mail_subject_template) || "");
    const [body, setBody] = React.useState((row && row.mail_text_template) || "");
    const [saving, setSaving] = React.useState(false);
    React.useEffect(() => {
        setSubject((row && row.mail_subject_template) || "");
        setBody((row && row.mail_text_template) || "");
    }, [row]);
    if (!row) {
        return <Text type="secondary">This campaign has no built-in email yet: reload the page.</Text>;
    }
    const defaults = builtinSignupDefaults(doubleOptIn);
    const dirty = subject !== (row.mail_subject_template || "") || body !== (row.mail_text_template || "");
    const save = () => {
        setSaving(true);
        OverwriteApi(getAccessTokenSilently, `/api/campaigns/${campaignId}/email/variants/${row.id}`,
            () => { setSaving(false); Message.success("Signup email saved"); onChanged(); },
            () => setSaving(false),
            (err) => { setSaving(false); Message.error((err && err.message) || "Couldn't save the signup email"); },
            { name: row.name, should_run: true, role: "signup", mail_subject_template: subject, mail_text_template: body, mail_html_template: "", editor_design: null });
    };
    return (
        <div>
            <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Subject</div>
            <Input value={subject} placeholder={defaults.subject} onChange={setSubject} />
            <div style={{ fontSize: 12, color: "var(--color-text-3)", margin: "10px 0 4px" }}>Message</div>
            <Input.TextArea value={body} placeholder={defaults.body} autoSize={{ minRows: 4 }} onChange={setBody} />
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>
                {doubleOptIn ? "The “Confirm my subscription” button goes below your message." : reward ? "The reward's button goes below your message." : ""}
                {" "}You can use {"{{ .Contact.Name }}"}. Leave blank to use the default.
            </Text>
            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                <Button type="primary" loading={saving} disabled={!dirty} onClick={save}>Save signup email</Button>
                {dirty && <Button onClick={() => { setSubject(row.mail_subject_template || ""); setBody(row.mail_text_template || ""); }}>Discard</Button>}
            </div>
        </div>
    );
}
